import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy } from '@angular/core';
import { Route, Router, RouterOutlet } from '@angular/router';
import { FuseSplashScreenService } from '@fuse/services/splash-screen';
import { environment } from '../environments/environment';
import { LoginService } from './services/login.service';
import { Preferences } from '@capacitor/preferences';
import { v4 as uuidv4 } from 'uuid';
import { AuthService } from './core/auth/auth.service';
import { Subject, takeUntil } from 'rxjs';
import {
  requestWebFCMToken,
  listenForMessages,
  startTokenRefreshListener,
  fcmForegroundMessage$,
  fcmTokenChanged$,
  getFcmMessageKey,
} from './firebase-messaging-init';
import { NotificationAlertService } from './services/notification-alert.service';
import { PushTokenSyncService } from './services/push-token-sync.service';
import { NotificationsService } from './layout/common/notifications/notifications.service';


@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [RouterOutlet],
})
export class AppComponent implements OnDestroy {
    /**
     * Constructor
     */
    Atoken:any = '';
    soundBlocked = false;
    private _destroy$ = new Subject<void>();
    private _serviceWorkerMessageHandler = (event: MessageEvent<any>) => {
      void this.handleServiceWorkerMessage(event);
    };
   constructor(
    private fuseSplashScreenService: FuseSplashScreenService,
    private service: LoginService,
    private router: Router,
      private service1: AuthService,
      private pushTokenSyncService: PushTokenSyncService,
        private notificationAlertService: NotificationAlertService,
        private notificationsService: NotificationsService
  ) {}

  ngOnInit() {
    // Initialize splash screen and anonymous token only, no navigation
    this.fuseSplashScreenService.show(); // Show splash
    
    // Initialize anonymous token if needed, but don't navigate
    this.initializeApp();
    
    // Hide splash screen after a delay
    setTimeout(() => {
      this.fuseSplashScreenService.hide();
    }, 1000);

    void this.pushTokenSyncService.syncCachedTokenIfNeeded();

    fcmTokenChanged$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      void this.pushTokenSyncService.syncCachedTokenIfNeeded();
    });

    fcmForegroundMessage$.pipe(takeUntil(this._destroy$)).subscribe((payload) => {
      this.notificationsService.ingestPushPayload(payload);

      // In the foreground FCM delivers via onMessage and may NOT fire the
      // service-worker 'push' event, so NEW_MESSAGE never arrives and the
      // alert would stay silent.  Trigger it here too — notifyIncoming()
      // dedupes by messageKey, so a later SW NEW_MESSAGE is ignored.
      const messageKey = getFcmMessageKey(payload);
      void this.isAuthenticatedSessionActive().then((active) => {
        if (active) {
          this.notificationAlertService.notifyIncoming(messageKey);
        }
      });
    });

    // Sound is managed exclusively through the service worker message channel
    // (NEW_MESSAGE → start, NOTIFICATION_CLOSED/CLICKED → stop) to guarantee
    // the same key is used for both start and stop.  fcmForegroundMessage$ is
    // still used by OrderHistoryComponent for auto-refresh.

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', this._serviceWorkerMessageHandler);
    }

    // Show a "tap to enable sound" banner when the browser's autoplay policy
    // is blocking the alert sound.  The banner disappears as soon as the
    // user interacts with the page (which unlocks the AudioContext).
    this.notificationAlertService.soundBlocked$
      .pipe(takeUntil(this._destroy$))
      .subscribe((blocked) => {
        this.soundBlocked = blocked;
      });

    void this.preloadFcmTokenForAuthenticatedSession();

    listenForMessages();
    startTokenRefreshListener();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', this._serviceWorkerMessageHandler);
    }
    this.notificationAlertService.reset();
  }

  private async initializeApp() {
    try {
      // Always get a fresh anonymous token on app initialization
      console.log('Requesting fresh anonymous token...');
      this.service.splashScreen().subscribe({
        next: (response: any) => {
          console.log('Splash screen response:', response);
          if (response && response.status === true && response.anonymousToken) {
            Preferences.set({ key: 'anonymouse', value: response.anonymousToken });
            console.log('Fresh anonymous token saved successfully');
          } else {
            console.error('Failed to get anonymous token from splash screen');
          }
        },
        error: (error: any) => {
          console.error('Splash screen API error:', error);
        }
      });
      
      // Let the routing system and AuthGuard handle navigation
    } catch (error) {
      console.error('Error initializing app:', error);
    }
  }



  async getToken(): Promise<string | null> {
    const { value } = await Preferences.get({ key: 'anonymouse'})
    return value;
  }

  private async isAuthenticatedSessionActive(): Promise<boolean> {
    const token = await this.service1.getToken();
    const normalizedToken = (token || '').trim();
    return Boolean(normalizedToken && normalizedToken !== 'null' && normalizedToken !== 'undefined');
  }

  private async handleServiceWorkerMessage(event: MessageEvent<any>): Promise<void> {
    const type = event.data?.type;
    const messageKey = event.data?.messageKey ?? event.data?.data?.messageKey;

    if (type === 'NEW_MESSAGE') {
      if (!(await this.isAuthenticatedSessionActive())) {
        console.log('Ignoring service-worker push sound trigger because no authenticated session is active.');
        return;
      }

      this.notificationAlertService.notifyIncoming(messageKey);
      this.notificationsService.ingestPushPayload(event.data?.data ?? {});
      return;
    }

    if (type === 'NOTIFICATION_CLOSED' || type === 'NOTIFICATION_CLICKED') {
      this.notificationAlertService.notifyResolved(messageKey);
    }
  }

  private async preloadFcmTokenForAuthenticatedSession(): Promise<void> {
    if (!(await this.isAuthenticatedSessionActive())) {
      return;
    }

    const token = await requestWebFCMToken();
    if (token) {
      console.log('FCM token cached in Preferences');
    }
  }

}
